import React, { useMemo } from 'react';
import TransformSection from './common/transform-section';
import { registerInspector } from './inspector-registry';
import ColorField from './shared/color-field';
import CornerRadiusSection from './shared/corner-radius-section';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import MatchFilterSection from './shared/match-filter-section';
import NumberField from './shared/number-field';
import VignetteSection from './shared/vignette-section';
import { rectCornerStrategy } from './shared/corner-radii-strategies';
import { useCommonFabricProp } from './shared/use-common-fabric-prop';
import { useCornerRadii } from './shared/use-corner-radii';
import { useVignette } from './shared/use-vignette';
import type { ObjectInspectorProps } from './types';

const MIXED = '(разное)';

const RectInspector: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const fill = useCommonFabricProp<string>(objects, 'fill', editor);
  const stroke = useCommonFabricProp<string>(objects, 'stroke', editor);
  const strokeWidth = useCommonFabricProp<number>(objects, 'strokeWidth', editor);
  const opacity = useCommonFabricProp<number>(objects, 'opacity', editor);
  const cornerStrategy = useMemo(() => rectCornerStrategy(editor), [editor]);
  const radii = useCornerRadii(objects, editor, cornerStrategy);
  const vignette = useVignette(objects, editor);
  // When the vignette is on, `fill` is a gradient (not a hex string), so the plain
  // fill picker is meaningless — the base color is edited in the Vignette section.
  const vignetteActive = vignette.enabled.value === true || vignette.enabled.isMixed;

  return (
    <>
      <TransformSection objects={objects} editor={editor} />
      <InspectorSection title="Внешний вид">
        {!vignetteActive && (
          <InspectorRow label="Заливка">
            <ColorField
              value={fill.isMixed ? undefined : fill.value}
              onChange={fill.setValue}
              placeholder={fill.isMixed ? MIXED : undefined}
            />
          </InspectorRow>
        )}
        <InspectorRow label="Обводка">
          <ColorField
            value={stroke.isMixed ? undefined : stroke.value}
            onChange={stroke.setValue}
            placeholder={stroke.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Толщина">
          <NumberField
            value={strokeWidth.isMixed ? undefined : strokeWidth.value}
            onChange={strokeWidth.setValue}
            min={0}
            placeholder={strokeWidth.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Прозрачность">
          <NumberField
            value={opacity.isMixed ? undefined : opacity.value}
            onChange={opacity.setValue}
            step={0.05}
            min={0}
            max={1}
            placeholder={opacity.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <CornerRadiusSection control={radii} />
      <VignetteSection control={vignette} />
      <MatchFilterSection objects={objects} editor={editor} />
    </>
  );
};

export default RectInspector;

registerInspector('rect', RectInspector);
